// websocketStore.ts
import { createRoot } from "solid-js";
import { type SetStoreFunction, createStore } from "solid-js/store";

import { postGetActivityDetails } from "@/api/clientApi/activity.ts";
import { GetMsgInfo } from "@/api/clientApi/notice.ts";
import { postGetReportContent } from "@/api/clientApi/report.ts";
import { toast } from "@/components/Base/Toaster/Toaster.tsx";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { ErrorNumberEnum } from "@/enum/request.ts";
import {
  getAllActivityDetails,
  useBounsCenterInit,
} from "@/hooks/bonusCenter/useBounsCenterInit.ts";
import { useConfigUpdate } from "@/hooks/useConfigUpdate.ts";
import { openSystemPopup } from "@/hooks/usePopup.ts";
import { useRepeatActivityContainBasic } from "@/hooks/useRepeatActivityContainBasic.ts";
import { useReport } from "@/hooks/useReport.ts";
import { useVipUpgrade } from "@/hooks/useVipUpgrade.ts";
import { getLangKey, useI18n } from "@/i18n";
import { LocaleMap } from "@/i18n/config.ts";
import { pubSub } from "@/lib/PubSub.ts";
import { clearToken, getWSSUrl } from "@/lib/apiUtils.ts";
import { isMobile } from "@/lib/isMobile.ts";
import {
  formatBaseToBind,
  formatToBindPoint,
  getCurPointType,
  pointFormatter,
} from "@/lib/pointUtils.ts";
import { routerUtils } from "@/lib/routerUtils.ts";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import { PubSubEvents } from "@/types/PubSub.ts";
import { ActivityTypeEnum } from "@/types/compontent/base/baseEnum.ts";
import { GetReportContentRsp } from "@/types/swagger/extReport.ts";
import { UserPoint } from "@/types/swagger/front.ts";

import { LocalStorageKey } from "../constants/constants.ts";
import { NetOnline } from "../constants/netEvent.ts";
import { CheckHelper } from "../lib/checkHelper.ts";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "../lib/localStorage.ts";
import { HelloReq, HelloRsp, PushMsg } from "../types/online.ts";
import { activityStore } from "./activityStore.ts";
import {
  clearBonusCenterStore,
  getSpecialActivityRewards,
} from "./bonusCenterStore.ts";
import { configStore } from "./configStore.ts";
import {
  clearUserStore,
  getUserInfo,
  updateCryptoUserPoint,
} from "./userStore.ts";

const { t } = useI18n();
const { showDialog, closeDialog } = useDialog();

export enum WebSocketConnectStatus {
  DISCONNECTED = -1,
  CONNECTING = 0,
  CONNECTED = 1,
}

export const ServerPushType = {
  TYPE_ONLINE_COUNT: 29999, //前端自定义：在线人数

  ONLINE_EVENT_POINT_CHANGE: 20003, // 用户金币变更
  ONLINE_EVENT_NOTICE: 20007, // 系统公告
  ONLINE_EVENT_THIRD_REPORT: 20031, // 有新的前端上报
  ONLINE_EVENT_PAY_SUCC: 20100, // 充值成功 参数是充值金额
  ONLINE_EVENT_PAY_BONUS: 20101, // 充值成功奖励到账 参数是奖励金额
  ONLINE_EVENT_PUT_SUCC: 20102, // 提现成功通知 参数是提现金额
  ONLINE_EVENT_PUT_FAIL: 20103, // 提现失败通知 参数是提现金额
  ONLINE_EVENT_VIP_UP: 20104, // vip升级通知 参数是最新vip等级
  // ONLINE_EVENT_FREE_BET: 20105, // 获得免费投注道具【无用，道具统一走20106】
  ONLINE_EVENT_GOT_ANY_PROMOITEM: 20106, // 获得任意道具
  ONLINE_EVENT_ACTIVITY_POINT: 20111, // 获得活动积分道具
  ONLINE_EVENT_CRYPTO_PAY_SUCC: 20200, // 加密货币充值成功通知, 参数包括充值金额和币种
  ONLINE_EVENT_CRYPOT_PUT_SUCC: 20201, // 加密货币提现成功通知, 参数包括充值金额和币种
  ONLINE_EVENT_CRYPTO_PUT_FAIL: 20202, // 加密货币提现失败通知, 参数包括充值金额和币种
  ONLINE_EVENT_LIMITATION: 20016, // 限制地理位置-可信半径

  ONLINE_EVENT_ISSUE_REWARD: 20112, // 游戏派彩【彩票通知】
  ONLINE_EVENT_ACTIVITY_REWARD: 20010, // 活动奖励领取

  ONLINE_EVENT_ACTIVITY_TO_DO: 20017, // 用户活动状态更新事件
  ONLINE_EVENT_SEND_MENU_REWARD: 20018, // 发放手动领取奖励包
  ONLINE_EVENT_TASK_OK: 20015, // 完成了新任务
  ONLINE_EVENT_BET_BONUS_OVER_ONCE: 20023, // 单次投注彩金金额超过限制

  //=============分割线=============
  ONLINE_EVENT_CONFIG_VERSION: 20032, // 配置更新
  ONLINE_EVENT_PASS_KYC: 20019, //KYC回调通知
  ONLINE_EVENT_SYSTEM_POPUP: 20022, //客户运营弹窗
  //以下为暂时无用的
  // TYPE_MARQEE_MSG: 20000, // 普通跑马灯消息
  // TYPE_MARQEE_PAY_BACK: 20013, // 充值返利跑马灯
  // TYPE_MARQEE_COMMI: 20014, // 可领取佣金
  // TYPE_VIP_UPDATE: 20011, //vip积分等级变化
  // TYPE_FINISH_TASK: 20015, // 完成任务
};

interface WebSocketStore {
  socket: WebSocket | null;
  messages: string[];
  attempts: number;
  socketUrl: string;
  lastReceiveTime: number;
  socketStatus: WebSocketConnectStatus;
  heartbeatInterval: ReturnType<typeof setInterval> | null;
  reconnectInterval: ReturnType<typeof setTimeout> | null;
  checkHelper: CheckHelper;
  msg_seq: number;
  nti_seq: number;
  curGid: number;
  curGUrl: string;
  curGBody: string;
}

// Define WebSocket message types
interface WebSocketMessage {
  _func?: string;
  _param?:
    | {
        _para?: unknown;
      }
    | HelloReq;
  _errno?: number;
  type?: string;
  data?: unknown;
  seq?: number;
  _check?: string;
  _mod?: string;
  _lang?: number;
  _token?: string;
}

interface NtiMessage {
  seq: number;
  type: number;
  data:
    | {
        point?: number;
        point_type?: number;
        /* eslint-disable @typescript-eslint/no-explicit-any */
      }
    | any;
}

export type PushMessage = NtiMessage | PushMsg;

interface Listener {
  id: string;
  listener: CommandListener;
}

// Replace any types with proper types
type CommandListener = (data: unknown, error: Error | null, id: string) => void;

// 事件处理器接口
type EventHandler = (item: PushMessage) => void;

interface EventHandlerConfig {
  handler: EventHandler;
  merge: boolean; // true: 合并处理，将整个数组传给处理器；false: 循环处理每个item
}

// 事件处理器注册表
const eventHandlers = new Map<number, EventHandlerConfig>();

export const [webSocketStore, setWebSocketStore] = createRoot(() => {
  const [store, setStore] = createStore<WebSocketStore>({
    socket: null,
    messages: [],
    attempts: 0,
    socketUrl: "",
    lastReceiveTime: 0,
    heartbeatInterval: null,
    reconnectInterval: null,
    checkHelper: new CheckHelper(),
    socketStatus: WebSocketConnectStatus.DISCONNECTED,
    msg_seq: 0,
    nti_seq: 0,
    curGid: 0,
    curGUrl: "",
    curGBody: "",
  });
  return [store, setStore] as const;
}) as [WebSocketStore, SetStoreFunction<WebSocketStore>];

const subscribers = new Map<string, Listener[]>();
const reconnectAttempts = 3;
const defaultTimeout = 6;
const timerInterval = 20;
const reconnectTimeout = 3000;
const maxPushMsgLen = 30;
let sendInterval = 0;
let lastHeartBeatInterval = 3000;
let socketReconnectReceiveCheckInteval = 30 * 1000;

// 注册事件处理器
const registerEventHandler = (
  eventType: number,
  handler: EventHandler,
  merge: boolean = false,
) => {
  eventHandlers.set(eventType, { handler, merge });
};

// 注销事件处理器
const unregisterEventHandler = (eventType: number) => {
  eventHandlers.delete(eventType);
};

// 获取事件处理器
const getEventHandler = (eventType: number): EventHandlerConfig | undefined => {
  return eventHandlers.get(eventType);
};

// 连接 WebSocket
const connectWebSocket = (url: string) => {
  console.log("[WebSocket] connectWebSocket url", url);

  setWebSocketStore({ socketUrl: url });
  if (webSocketStore.socketStatus == WebSocketConnectStatus.CONNECTING) {
    console.log(
      "[WebSocket] connectWebSocket current == WebSocketConnectStatus.CONNECTING ",
      url,
    );
    return;
  }
  setWebSocketStore({ socketStatus: WebSocketConnectStatus.CONNECTING });
  closeWebSocket();
  if (webSocketStore.socket) {
    webSocketStore.socket.close(1000, "Normal Closure");
    webSocketStore.socket.onopen = null;
    webSocketStore.socket.onmessage = null;
    webSocketStore.socket.onerror = null;
    webSocketStore.socket.onclose = null;
    setWebSocketStore({ socket: null });
  }
  subscribers.clear();
  const ws = new WebSocket(url);

  ws.onopen = () => {
    console.log(
      "[WebSocket] connectWebSocket current == WebSocketConnectStatus.CONNECTED ",
    );
    setWebSocketStore({
      socket: ws,
      attempts: 0,
      socketStatus: WebSocketConnectStatus.CONNECTED,
      lastReceiveTime: Date.now(),
    });
    startHeartbeat();
  };
  ws.onclose = (evt: CloseEvent) => {
    console.log(
      "[WebSocket]  connection closed, attempting to reconnect...",
      evt.code,
      evt.reason,
    );

    setWebSocketStore({
      socketStatus: WebSocketConnectStatus.DISCONNECTED,
    });
    if (webSocketStore.reconnectInterval) {
      clearTimeout(webSocketStore.reconnectInterval);
    }
    if (webSocketStore.attempts < reconnectAttempts) {
      setWebSocketStore({ attempts: webSocketStore.attempts + 1 });

      const reconnectInterval = setTimeout(
        () => connectWebSocket(url),
        reconnectTimeout,
      );
      setWebSocketStore({
        reconnectInterval: reconnectInterval,
      });
    }
  };
  ws.onerror = (error) => {
    console.error("WebSocket Error:", error);
    setWebSocketStore({
      socketStatus: WebSocketConnectStatus.DISCONNECTED,
    });
  };

  ws.onmessage = (message) => {
    const data = JSON.parse(message.data);
    setWebSocketStore({ lastReceiveTime: Date.now() });
    if (data && data._func) {
      //第一个心跳包未回包
      const svrTimeout =
        (data._param._para && data._param._para.timeout) || defaultTimeout;
      sendInterval = svrTimeout * 1000 || 3 * 1000;
      lastHeartBeatInterval = sendInterval;
      socketReconnectReceiveCheckInteval = sendInterval * 2 + 3000;

      if (data._func === NetOnline.Hello) {
        // TODO:心跳命令解析
        if (data._param?._para) {
          const helloRsp: HelloRsp = data._param?._para as HelloRsp;
          //更新 msg_seq nti_seq
          const localHelloRsp: HelloRsp = helloRsp;
          if (helloRsp.msg_seq || helloRsp.nti_seq) {
            if (!helloRsp.msg_seq) {
              localHelloRsp.msg_seq = webSocketStore.msg_seq;
            } else {
              //   webSocketStore.msg_seq = helloRsp.msg_seq;
              setWebSocketStore({ msg_seq: helloRsp.msg_seq });
            }
            if (!helloRsp.nti_seq) {
              localHelloRsp.nti_seq = webSocketStore.nti_seq;
            } else {
              //   webSocketStore.nti_seq = helloRsp.nti_seq;
              setWebSocketStore({ nti_seq: helloRsp.nti_seq });
            }
            setLocalStorageItem(
              LocalStorageKey.HELLO,
              JSON.stringify(localHelloRsp),
            );
          }
          //获取本地Push 缓存
          const localStoragePushMsgObj = getLocalStorageMsgList();
          const ntisAndMsgs = helloRsp.ntis || helloRsp.msgs || [];

          // 按事件类型分组处理
          const messagesByType = new Map<number, PushMessage[]>();
          ntisAndMsgs.forEach((item: PushMessage) => {
            if (item.seq && !localStoragePushMsgObj.includes(item.seq)) {
              if (item.type) {
                const existing = messagesByType.get(item.type) || [];
                existing.push(item);
                messagesByType.set(item.type, existing);
              }
            }
            if (!item?.seq && item.type) {
              const existing = messagesByType.get(item.type) || [];
              existing.push(item);
              messagesByType.set(item.type, existing);
            }
          });

          // 根据 merge 配置处理消息
          messagesByType.forEach((messages, eventType) => {
            const handlerConfig = getEventHandler(eventType);
            if (handlerConfig) {
              if (handlerConfig.merge) {
                // 合并处理：只调用一次处理器，传入第一个消息（或根据业务需求处理）
                // 注意：合并模式下，如果需要处理所有消息，应该在处理器内部实现
                if (messages.length > 0) {
                  handlerConfig.handler(messages[0]);
                }
                // 处理序列号存储
                messages.forEach((item) => {
                  if (item.seq) {
                    const localStoragePushMsgObj = getLocalStorageMsgList();
                    if (localStoragePushMsgObj.length >= maxPushMsgLen) {
                      localStoragePushMsgObj.shift();
                    }
                    localStoragePushMsgObj.push(item.seq);
                    setLocalStorageItem(
                      LocalStorageKey.PUSH_MSG,
                      JSON.stringify(localStoragePushMsgObj),
                    );
                  }
                });
              } else {
                // 非合并处理：循环处理每个item
                messages.forEach((item) => {
                  dealNtiMsg(item);
                });
              }
            } else {
              // 未注册的处理器，使用默认处理方式
              messages.forEach((item) => {
                dealNtiMsg(item);
              });
            }
          });
        }
      } else if (data._func === NetOnline.Push) {
        // TODO:心跳命令解析
        if (data._param?._para) {
          const pushRsp: PushMsg = data._param?._para as PushMsg;
          //获取本地Push 缓存
          const localStoragePushMsgObj = getLocalStorageMsgList();
          if (pushRsp.seq && !localStoragePushMsgObj.includes(pushRsp.seq)) {
            dealNtiMsg(pushRsp);
          }
        }
      } else {
        //删除非心跳 check
        if (webSocketStore.checkHelper.getFuncChecker(data._check)) {
          webSocketStore.checkHelper.removeFuncChecker(data._check);
        }
        ntiFunc(data);
      }
    } else {
      handleError(data);
    }
    // subscribers.forEach((callback) => callback(message)); // 触发订阅者回调
  };
};
const getLocalStorageMsgList = (): number[] => {
  const data = localStorage.getItem(LocalStorageKey.PUSH_MSG);
  if (data) {
    try {
      return JSON.parse(data) as number[];
    } catch (error) {
      console.error("Error parsing localStorage data:", error);
      return [];
    }
  }
  return [];
};

const ntiFunc = (_data: WebSocketMessage) => {
  // let listeners = eventListenersRef.current.get(data._func) || [];
  // listeners.forEach(({ id, listener }) => {
  //   if (data._param?._para) {
  //     listener(data._param?._para, null, id);
  //   } else {
  //     listener(null, data, id);
  //   }
  // });
};

//处理错误码
const handleError = (data: WebSocketMessage) => {
  if (data && data._errno) {
    const errno = data._errno;
    //   toast({ desc: "Error " + errno, type: "error" });
    if (
      errno === ErrorNumberEnum.TokenErrorDel ||
      errno === ErrorNumberEnum.TokenError
    ) {
      console.error("[WebSocket] handleError 401");
      clearToken();
      clearBonusCenterStore();
      closeWebSocket();
      clearUserStore();
      routerUtils.push("/login");

      // // TODO:跳转登录
      // navigation.navigate(RouterTypeEnum.Login as never);
    }
  }
};

// 事件处理函数定义
const handlePointChange = (item: PushMessage) => {
  console.log(
    "[WebSocket] Received ONLINE_EVENT_POINT_CHANGE message1222:",
    item,
  );
  if (item?.data) {
    updateCryptoUserPoint(item.data as UserPoint);
    pubSub.publish(PubSubEvents.UpdateLiveStatus);
  }
};

const handleNotice = (item: PushMessage) => {
  popupSystemToast(item?.data as any);
};

const handlePaySucc = (item: PushMessage) => {
  console.log("[WebSocket] Received message: ONLINE_EVENT_PAY_SUCC ", item);
  const messageData = item?.data as any;
  if (messageData?.point) {
    const depositPoint = formatBaseToBind(messageData?.point ?? 0);
    toast.notice(
      t("tips.notice_deposit_cash", {
        symbol: depositPoint.symbol,
        value: depositPoint.value,
      }),
    );
    getUserInfo();
    handleActivityUpdate(item, false);
    getSpecialActivityRewards();
  }
};

const handlePayBonus = (item: PushMessage) => {
  console.log("[WebSocket] Received message: ONLINE_EVENT_PAY_BONUS ", item);
  const messageData = item?.data as any;
  if (messageData.point) {
    const depositPoint = formatBaseToBind(messageData?.point ?? 0);
    toast.notice(
      t("tips.notice_deposit_bonus", {
        symbol: depositPoint.symbol,
        value: depositPoint.value,
      }),
    );
    getUserInfo();
  }
};

const handlePutSucc = (item: PushMessage) => {
  console.log("[WebSocket] Received message: ONLINE_EVENT_PUT_SUCC ", item);
  const messageData = item?.data as any;
  if (messageData.point) {
    const depositPoint = formatBaseToBind(messageData?.point ?? 0);
    toast.notice(
      t("tips.notice_withdrawal_successful", {
        symbol: depositPoint.symbol,
        value: depositPoint.value,
      }),
    );
    getUserInfo();
  }
};

const handlePutFail = (item: PushMessage) => {
  console.log("[WebSocket] Received message: ONLINE_EVENT_PUT_FAIL ", item);
  const messageData = item?.data as any;
  if (messageData.point) {
    const depositPoint = formatBaseToBind(messageData?.point ?? 0);
    toast.error(
      t("tips.notice_withdrawal_failed", {
        symbol: depositPoint.symbol,
        value: depositPoint.value,
      }),
    );
    getUserInfo();
  }
};

const handleCryptoPaySucc = (item: PushMessage) => {
  console.log(
    "[WebSocket] Received message: ONLINE_EVENT_CRYPTO_PAY_SUCC ",
    item,
  );
  const messageData = item?.data as any;
  if (activityStore.currentActivity == ActivityTypeEnum.firstDeposit) {
    closeDialog(DialogType.ActivityIframe);
  }
  if (messageData.point_type == getCurPointType()) {
    const depositPoint = messageData.point ?? 0;
    toast.notice(
      t("reward.congrats_deposit", {
        val: pointFormatter(depositPoint),
      }),
    );
  }
  getUserInfo();
  handleActivityUpdate(item, true);
  getSpecialActivityRewards();
  closeDialog(DialogType.DepositIframe);
};

const handleCryptoPutSucc = (item: PushMessage) => {
  console.log(
    "[WebSocket] Received message: ONLINE_EVENT_CRYPOT_PUT_SUCC ",
    item,
  );
  getUserInfo();
};

const handleCryptoPutFail = (item: PushMessage) => {
  console.log(
    "[WebSocket] Received message: ONLINE_EVENT_CRYPTO_PUT_FAIL ",
    item,
  );
  const messageData = item?.data as any;
  if (messageData.point_type == getCurPointType()) {
    // const putPoint = messageData.point ?? 0;
  }
};

const handleThirdReport = (_item: PushMessage) => {
  reportEvent();
};

const handleConfigVersion = (item: PushMessage) => {
  updateConfig(item);
};

const handleActivityToDo = (item: PushMessage) => {
  handleActivityUpdate(item, false);
  const messageData = item?.data as any;
  if (messageData === ActivityTypeEnum.reliefFund3) {
    showDialog({
      type: DialogType.ActivityIframe,
      data: { atype: 19 },
    });
  }
};

const handleSendMenuReward = (item: PushMessage) => {
  handleActivityUpdate(item, false);
};

const handleTaskOk = (item: PushMessage) => {
  handleActivityUpdate(item, false);
};

const handleVipUp = (item: PushMessage) => {
  console.log("[WebSocket] Received message: ONLINE_EVENT_VIP_UP ", item);
  useVipUpgrade().handleVipLevelUp(item);
};

const handleGotAnyPromoItem = (item: PushMessage) => {
  console.log(
    "[WebSocket] Received message: ONLINE_EVENT_GOT_ANY_PROMOITEM ",
    item,
  );
  const messageData = (item?.data as any) || [];
  if (messageData && messageData.length > 0) {
    const newArr: any[] = JSON.parse(JSON.stringify(messageData));
    console.log("newArr", newArr);
    newArr.forEach((promoItem) => {
      if (promoItem.config) {
        promoItem.config = JSON.parse(promoItem.config);
        promoItem.activity_cfg = promoItem.config;
      }
      if (promoItem.item_lang) {
        promoItem.item_lang = JSON.parse(promoItem.item_lang);
      }
    });
    const { showDialog } = useDialog();
    showDialog({
      type: DialogType.Reward,
      data: {
        items: newArr,
      },
    });
    const { getPropsItemData } = useBounsCenterInit();
    getPropsItemData(true);
  }
};

const handleActivityPoint = (item: PushMessage) => {
  console.log(
    "[WebSocket] Received message: ONLINE_EVENT_ACTIVITY_POINT ",
    item,
  );
  const messageData = item?.data as any;
  if (messageData?.config) {
    const config = JSON.parse(messageData?.config);
    const item_lang = JSON.parse(messageData?.item_lang);
    showDialog({
      type: DialogType.Reward,
      data: {
        items: [
          {
            ...messageData,
            activity_cfg: config,
            item_lang: item_lang,
          },
        ],
      },
    });
    const { getPropsItemData } = useBounsCenterInit();
    getPropsItemData(true);
  }
};

const handleBetBonusOverOnce = (_item: PushMessage) => {
  showDialog({
    type: DialogType.Tips,
    data: {
      content: t("bonus.redeem_rules_5", {
        val: formatToBindPoint({
          value: sysConfigStore.sysConfigData.bet_bonus_max_once,
          isReward: true,
          isConverted: false,
        }),
      }),
      title: t("common.tips"),
      confirmText: t("common.confirm"),
    },
  });
};

const handleSystemPopup = (item: PushMessage) => {
  openSystemPopup(1, item?.data as number);
};

// 处理单个推送消息
const dealNtiMsg = (item: PushMessage) => {
  console.log(item);
  if (item.type) {
    const handlerConfig = getEventHandler(item.type);
    if (handlerConfig) {
      handlerConfig.handler(item);
    } else {
      console.warn(`[WebSocket] 未注册的事件处理器: ${item.type}`);
    }
    //处理和存储推送序列
    if (item.seq) {
      const localStoragePushMsgObj = getLocalStorageMsgList();
      if (localStoragePushMsgObj.length >= maxPushMsgLen) {
        localStoragePushMsgObj.shift();
      }
      localStoragePushMsgObj.push(item.seq);
      setLocalStorageItem(
        LocalStorageKey.PUSH_MSG,
        JSON.stringify(localStoragePushMsgObj),
      );
    }
  }
};
// 发送消息
const sendMessage = (message: string) => {
  if (
    webSocketStore.socket &&
    webSocketStore.socket.readyState === WebSocket.OPEN
  ) {
    webSocketStore.socket.send(message);
  } else {
    console.warn("WebSocket is not connected.");
  }
};

const startHeartbeat = () => {
  sendHeatBeat(true);
  startTimer();
};

const clearTimer = () => {
  if (webSocketStore.heartbeatInterval) {
    clearInterval(webSocketStore.heartbeatInterval);
    setWebSocketStore({ heartbeatInterval: null });
  }
};

const startTimer = () => {
  clearTimer();
  //启动定时器发送
  const heartbeatInterval = setInterval(() => {
    const dt = timerInterval;

    // 判断是否超时
    if (
      webSocketStore.socket?.readyState === WebSocket.OPEN &&
      webSocketStore.lastReceiveTime != 0 &&
      webSocketStore.socketStatus != WebSocketConnectStatus.CONNECTING
    ) {
      if (
        Date.now() - webSocketStore.lastReceiveTime >
        socketReconnectReceiveCheckInteval
      ) {
        console.log(
          "[checker]  enter  reconnect heat beat timeout ",
          webSocketStore.lastReceiveTime,
        );
        reconnectWS();
        return;
      }
    }
    // 判断checker 是否丢失
    if (webSocketStore.socket?.readyState === WebSocket.OPEN) {
      if (webSocketStore.checkHelper.checkFuncTimeout(dt)) {
        // LogUtils.log("[checker] enter  reconnect checkFuncTimeout enter");
        reconnectWS();
        return;
      }
    }
    //发送心跳
    if (sendInterval > 0) {
      sendInterval -= dt;
      if (sendInterval <= 0) {
        // console.log("[checker] sendInterval <= 0 enter sendHeatBeat ");
        sendHeatBeat();
        sendInterval = lastHeartBeatInterval;
      }
    }
  }, timerInterval);
  setWebSocketStore({ heartbeatInterval: heartbeatInterval });
};

const sendHeatBeat = async (hasToken: boolean = false) => {
  if (webSocketStore.socket?.readyState != WebSocket.OPEN) {
    console.log(
      "[WebSocket] startHeartbeat websocket.readyState ",
      webSocketStore.socket?.readyState,
    );
  }

  if (webSocketStore.socket?.readyState === WebSocket.OPEN) {
    const data: WebSocketMessage = {};
    let msgseq = 0;
    let ntiseq = 0;

    const msgStr = getLocalStorageItem(LocalStorageKey.HELLO, {});

    if (msgStr) {
      const msgJsonObj = msgStr as HelloRsp;
      msgseq = msgJsonObj?.msg_seq || 0;
      ntiseq = msgJsonObj?.nti_seq || 0;
    }

    webSocketStore.checkHelper.updateChecker();
    data._check = webSocketStore.checkHelper.getNormalChecker();
    const param: HelloReq = {
      game_id: webSocketStore.curGid || 0,
      max_msg: 0,
      msg_seq:
        webSocketStore.msg_seq > msgseq ? webSocketStore.msg_seq : msgseq,
      nti_seq:
        webSocketStore.nti_seq > ntiseq ? webSocketStore.nti_seq : ntiseq,
    };
    data._param = param;
    data._func = NetOnline.Hello;
    data._mod = NetOnline.mod;
    data._lang = 11; // TODO: 替换当前选择语言
    const token = getLocalStorageItem(LocalStorageKey.TOKEN, "");
    if (token) {
      if (hasToken) {
        data._token = token;
      } else {
        data._token = "pass";
      }
    } else {
      data._token = "pass";
    }
    // LogUtils.log("[WebSocket] sendHeatBeat ", JSON.stringify(data));
    webSocketStore.socket?.send(JSON.stringify(data));
  }
};
// 订阅 WebSocket 消息
const subscribe = (_callback: (message: string) => void) => {
  //   subscribers.add(callback);
  //   return () => subscribers.delete(callback);
};
const reconnectWS = () => {
  connectWebSocket(getWSSUrl());
};
// 断开 WebSocket 连接
const closeWebSocket = (
  code: number = 1000,
  reason: string = "Normal Closure",
) => {
  if (webSocketStore.socket) {
    webSocketStore.socket.close(code, reason);
    webSocketStore.socket.onopen = null;
    webSocketStore.socket.onmessage = null;
    webSocketStore.socket.onerror = null;
    webSocketStore.socket.onclose = null;
    setWebSocketStore({ socket: null });
    if (webSocketStore.reconnectInterval) {
      clearTimeout(webSocketStore.reconnectInterval);
    }
    //   if (eventListenersRef.current) {
    //     eventListenersRef.current.clear();
    //   }
    webSocketStore.checkHelper.clearAllFuncChecker();
  }
};

// 注册所有事件处理器
// 默认 merge=false，表示循环处理每个消息
registerEventHandler(
  ServerPushType.ONLINE_EVENT_POINT_CHANGE,
  handlePointChange,
  false,
);
registerEventHandler(ServerPushType.ONLINE_EVENT_NOTICE, handleNotice, false);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_PAY_SUCC,
  handlePaySucc,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_PAY_BONUS,
  handlePayBonus,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_PUT_SUCC,
  handlePutSucc,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_PUT_FAIL,
  handlePutFail,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_CRYPTO_PAY_SUCC,
  handleCryptoPaySucc,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_CRYPOT_PUT_SUCC,
  handleCryptoPutSucc,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_CRYPTO_PUT_FAIL,
  handleCryptoPutFail,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_THIRD_REPORT,
  handleThirdReport,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_CONFIG_VERSION,
  handleConfigVersion,
  true,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_ACTIVITY_TO_DO,
  handleActivityToDo,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_SEND_MENU_REWARD,
  handleSendMenuReward,
  false,
);
registerEventHandler(ServerPushType.ONLINE_EVENT_TASK_OK, handleTaskOk, false);
registerEventHandler(ServerPushType.ONLINE_EVENT_VIP_UP, handleVipUp, false);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_GOT_ANY_PROMOITEM,
  handleGotAnyPromoItem,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_ACTIVITY_POINT,
  handleActivityPoint,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_BET_BONUS_OVER_ONCE,
  handleBetBonusOverOnce,
  false,
);
registerEventHandler(
  ServerPushType.ONLINE_EVENT_SYSTEM_POPUP,
  handleSystemPopup,
  false,
);

// 导出 store
export {
  // webSocketStore,
  sendMessage,
  subscribe,
  closeWebSocket,
  connectWebSocket,
  reconnectWS,
  registerEventHandler,
  unregisterEventHandler,
};

const popupSystemToast = async (id: any) => {
  if (!id) return;
  const res = await GetMsgInfo({
    id: id,
    msg_type: 1,
  });
  if (res?.msg_info) {
    const lang = JSON.parse(res?.msg_info?.lang_info);
    toast.publish(
      lang?.[getLangKey()] ? lang?.[getLangKey()]?.title : lang?.[LocaleMap.en],
      lang?.[getLangKey()]
        ? lang?.[getLangKey()]?.content
        : lang?.[LocaleMap.en],
    );
  }
};

const handleActivityUpdate = async (msg: any, webToApp: boolean) => {
  try {
    if (
      msg?.data &&
      (typeof msg?.data === "string" ||
        (typeof msg?.data === "number" && !isNaN(msg?.data)))
    ) {
      postGetActivityDetails({ atypes: [Number(msg.data)] })?.then(() => {
        // 處理 web to app 活動獎勵
        if (webToApp && isMobile())
          useRepeatActivityContainBasic().getWebToAppActivityAward();
      });
    } else {
      getAllActivityDetails()?.then(() => {
        // 處理 web to app 活動獎勵
        if (webToApp && isMobile())
          useRepeatActivityContainBasic().getWebToAppActivityAward();
      });
    }
  } catch (error) {
    console.error("handleActivityUpdate error", error);
  }
};

const updateConfig = async (msg: any) => {
  const { updateConfigVersion } = useConfigUpdate();
  console.log("updateConfig msg", msg);
  try {
    const newVersion = msg.data;
    if (configStore.version && newVersion > configStore.version) {
      updateConfigVersion();
    }
  } catch (error) {
    console.error("handleConfigVersion error", error);
  }
};

export const reportEvent = async () => {
  try {
    const res = await postGetReportContent();
    const { handleAllReport } = useReport();
    if (res) {
      handleAllReport(res as GetReportContentRsp);
    }
  } catch (error) {
    console.error("reportEvent error", error);
  }
};
